<script lang="ts">
import AlertDialog from '@/app/common/components/AlertDialog.vue';
import Breadcrumb from '@/app/common/components/Breadcrumb.vue';
import ConfirmDialog from '@/app/common/components/ConfirmDialog.vue';
import DetailView from './detailView.vue';
import DetailAsc from '@/components/pages/admin/complaint-list/asc/detail.vue';
import DetailDpm from '@/components/pages/admin/complaint-list/dpm/detail.vue';
import Flight from '@/components/pages/complaint/detail/steps/flight.vue';
import Passenger from '@/components/pages/complaint/detail/steps/passenger.vue';
import Topic from '@/components/pages/complaint/detail/steps/topic.vue';
import RequestHeader from '@/components/pages/complaint/detail/header.vue';
import Summary from '@/components/pages/complaint/detail/summary.vue';
import DetailAirline from '@/components/pages/admin/complaint-list/airline/detail.vue';
import html2pdf from 'html2pdf.js';
import { complaintDetailBreadcrumb } from '@/components/utils';
import { ComplaintDetail, ComplaintFlight, ComplaintInfo } from '@/interface/Complaint.interface';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { FormStep, FormStepItem } from '@/interface/Form.interface';
import { FORMAT_CHANNEL, REQUEST_STATUS, STEP3_SECTION, STEP_THREE } from '@/app/const';
import { useSharedDataStore } from '@/store/app';
import { SelectionFEModelType } from '@/app/common/types/selection.type';
import { BaseAlert } from '@/interface/BaseAlert.interface';
import { BaseConfirm } from '@/interface/BaseConfirm.interface';
import { BreadcrumbType } from '@/app/common/types/breadcrumb.type';
import { complaintService, fileService, formService, userService } from '@/services/serviceProvider';
import { ComplaintBuyTicketSave, ComplaintFlightSave, ComplaintInfoSave } from '@/interface/SaveComplaint.interface';
import { UploadResponse } from '@/interface/FileUpload.interface';
import { AttachmentUploaderType, AttachmentUrl } from '@/app/common/types/attachUploader';
import { PassengerInfo } from '@/interface/Passenger.interface';

export default {
    components: {
        ConfirmDialog,
        AlertDialog,
        Breadcrumb,
        Flight,
        Passenger,
        Topic,
        DetailView,
        DetailAsc,
        DetailDpm,
        RequestHeader,
        Summary,
        DetailAirline
    },
    setup() {
        const router = useRouter();
        const currentRoute = router.currentRoute.value;
        const userSv = userService;
        const step = ref(1);
        // forms
        const flightForm = ref();
        const passengerForm = ref();
        const topicForm = ref();
        // components
        const flight = ref();
        const passenger = ref();
        const topic = ref();
        const dtAsc = ref();
        const dtDpm = ref();
        const dtAir = ref();
        // store
        const store = useSharedDataStore();
        const pdfContent = ref();

        return {
            router, currentRoute, step, flightForm, passengerForm,
            topicForm, flight, passenger, topic, dtAsc, dtDpm, dtAir,
            store, pdfContent, userSv
        }
    },
    data() {
        return {
            breadcrumb: [] as BreadcrumbType[],
            loading: false,
            isSubmitting: false,
            isMovingNext: false,
            today: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 0, 0, 0) as Date,
            complaintInfo: undefined as ComplaintInfo | undefined,
            activeStep: 1,
            flightStepNo: undefined as number | undefined,
            passengerStepNo: undefined as number | undefined,
            complaintStepNo: undefined as number | undefined,
            requestNoFromRoute: this.$route.params.requestNo,
            currentRequestNo: undefined as string | undefined,
            formStepComplaint: [] as FormStepItem[],
            successAlert: {
                display: false,
                success: true
            } as BaseAlert,
            failAlert: {
                display: false,
                success: false
            } as BaseAlert,
            confirm: {
                display: false,
            } as BaseConfirm,
            summary: {
                alert: false,
                header: undefined as string | undefined,
                callBack: undefined as any | undefined,
            },
            image: undefined as any,
            displayLoading: false as boolean,
            lang: this.$i18n.locale,
        }
    },
    watch: {
        activeStep(newVal, oldVal) {
            this.resetBreadcrumb();
        },
        async $route(to, from) {
            this.requestNoFromRoute = this.$route.params.requestNo;
            await this.initialRequest();
        },
        '$i18n.locale': function (newVal, oldVal) {
            this.lang = newVal;
        }
    },
    computed: {
        validFlightNoFormat() {
            if (this.complaintInfo?.flight?.flightNo){
                console.log('validFlightNoFormat', /[^a-zA-Z0-9]/g.test(this.complaintInfo.flight.flightNo));
                return /^[a-zA-Z0-9]+$/g.test(this.complaintInfo.flight.flightNo);
            }
            return false;
        },
        validFlight(): boolean {
            return this.complaintInfo !== undefined
                && this.complaintInfo.flight !== undefined
                && this.complaintInfo.flight !== null
                && this.complaintInfo.flight.departureDate !== undefined
                && this.complaintInfo.flight.departureDate !== null
                && this.complaintInfo.flight.flightNo !== undefined
                && this.complaintInfo.flight.flightNo !== null
                && this.validFlightNoFormat;
        },
        validBookingNo(): boolean {
            return this.complaintInfo != undefined
                && this.complaintInfo.flight !== undefined
                && this.complaintInfo.flight.bookings !== undefined
                && this.complaintInfo.flight.bookings.length > 0
                && this.complaintInfo.flight.bookings.filter(o => o.attachUploads && o.attachUploads.length > 0
                    || o.attachments && o.attachments.length > 0).length
                === this.complaintInfo.flight.bookings.length;
        },
        validTicketChannel(): boolean {
            if (this.complaintInfo && this.complaintInfo.flight) {
                const validChannel = this.complaintInfo.flight.channel !== undefined
                    && this.complaintInfo.flight.channel.mainTicketChannelId !== undefined;
                const validSelection = this.complaintInfo.flight.channel.mainTicketChannelOptional
                    ? this.complaintInfo.flight.channel.mainTicketFormatChannelOptional === FORMAT_CHANNEL.manual
                        ? this.complaintInfo.flight.channel.remark !== undefined
                        && this.complaintInfo.flight.channel.remark !== null
                        : this.complaintInfo.flight.channel.subTicketChannelId !== undefined
                    : true;
                return validChannel && validSelection;
            }
            return false;
        },
        validPassenger() {
            return this.complaintInfo
                && this.complaintInfo.passengers
                && this.complaintInfo.passengers.length > 0;
        },
        validTopic() {
            return this.complaintInfo
                && this.complaintInfo.detail
                && this.complaintInfo.detail.claims
                && this.complaintInfo.detail.claims.length > 0;
        },
        validBaggage(): boolean {
            if ((this.complaintInfo?.form?.stepThree ?? []).includes(STEP_THREE.Baggage)) {
                return this.complaintInfo?.detail?.baggageComplaint !== undefined
                    && this.complaintInfo?.detail?.baggageComplaint !== null
            }
            return true;
        },
        disabled() {
            if (this.activeStep === this.flightStepNo) {
                return !this.validFlight || !this.validBookingNo || !this.validTicketChannel;
            } else if (this.activeStep === this.passengerStepNo) {
                return !this.validPassenger;
            }
            return false;
        },
        completed() {
            return !this.disabled;
        },
        viewMode() {
            if (this.complaintInfo && this.complaintInfo.requestNo)
                return this.complaintInfo.status !== REQUEST_STATUS.DRAFT;
            return false;
        },
        isNew() {
            return this.requestNoFromRoute === undefined;
        },
        haveFlightStep(): boolean {
            const step1 = this.complaintInfo?.form?.stepOne;
            return step1 && step1.length > 0 || false;
        },
        havePassengerStep(): boolean {
            const step2 = this.complaintInfo?.form?.stepTwo;
            return step2 && step2.length > 0 || false;
        },
        haveComplaintStep(): boolean {
            const step3 = this.complaintInfo?.form?.stepThree;
            return step3 && step3.length > 0 || false;
        },
        stepRequest() {
            return this.formStepComplaint.filter(f => f.name.startsWith(STEP3_SECTION.REQUEST) && f.name !== STEP3_SECTION.REQUEST);
        },
        isMainDpm(): boolean {
            return this.complaintInfo?.relatedMainDepartment?.departmentCode === userService.currentDepartment();
        },
        isSubDpm(): boolean {
            const dpmCodes = this.complaintInfo?.relatedSubDepartments?.map(o => o.departmentCode);
            const currentDepartment = userService.currentDepartment();
            if (dpmCodes && currentDepartment) return dpmCodes.includes(currentDepartment);
            return false;
        },
        isAscWithNotResponsible(): boolean {
            if (userService.isAsc()) {
                let responsible = undefined as string | undefined;
                if (this.isMainDpm && this.complaintInfo?.relatedMainDepartment?.departmentCode === 'ASC') {
                    responsible = this.complaintInfo.relatedMainDepartment?.responiblePersons?.find(o => o.activeResponsible)?.employeeId
                } else if (this.isSubDpm && this.complaintInfo?.relatedSubDepartments?.map(o => o.departmentCode).includes('ASC')) {
                    responsible = this.complaintInfo.relatedSubDepartments.find(o => o.departmentCode === 'ASC')?.responiblePersons?.find(rs => rs.activeResponsible)?.employeeId;
                }
                return responsible !== userService.currentUserInfo()?.employeeId;
            }
            return false;
        }
    },
    async mounted() {
        await this.initialRequest();
    },
    methods: {
        initBreadcrumb() {
            let results = [];
            let stepNo = 1;
            if (this.haveFlightStep) {
                const s = complaintDetailBreadcrumb.find(o => o.stepNo === 1);
                if (s) results.push({ ...s });
                this.flightStepNo = stepNo;
                stepNo++;
            }
            if (this.havePassengerStep) {
                const s = complaintDetailBreadcrumb.find(o => o.stepNo === 2);
                if (s) results.push({ ...s });
                this.passengerStepNo = stepNo;
                stepNo++;
            }
            if (this.haveComplaintStep) {
                const s = complaintDetailBreadcrumb.find(o => o.stepNo === 3);
                if (s) results.push({ ...s });
                this.complaintStepNo = stepNo;
            }
            results.forEach((o, index) => {
                o.stepNo = index + 1;
                o.goToStep = index + 1;
            });
            this.breadcrumb = results;
        },
        async initialRequest() {
            // get all steps
            await this.getFormStepComplaint();
            // initial request
            if (!this.isNew) {
                await this.getComplaintInfo();
                if (this.store.myData && this.store.myData.reloadDoc) {
                    if (this.haveFlightStep && this.flight) {
                        await this.flight.getTicketChannels();
                    }
                    this.activeStep++;
                }
            } else {
                // case NEW use id from url-params
                this.complaintInfo = { ...this.emptyForm() };
                this.complaintInfo.mainId = parseInt(this.currentRoute.params.mainTopicId.toString());
                this.complaintInfo.subId = parseInt(this.currentRoute.params.subTopicId.toString());
                await this.getSubTopic(parseInt(this.currentRoute.params.subTopicId.toString()));
                this.emptyStep(this.complaintInfo);
            }
            // initial breadcrumb
            this.initBreadcrumb();
            this.resetBreadcrumb();
        },
        async getComplaintInfo() {
            this.loading = true;
            try {
                // case open saved document use requestNo from url-params
                const requestNo = this.requestNoFromRoute.toString();
                this.displayLoading = true;
                const res = await complaintService.getRequestByRequestNo(requestNo);
                if (res && res.status == 200) {
                    this.displayLoading = false;
                    let data = res.data;
                    data.form = data.subTopicInfo.formInfo;
                    if (data.flight?.departureDate) {
                        data.flight.departureDate = new Date(data.flight.departureDate);
                        data.flight.departureDateString = complaintService.dateString(data.flight.departureDate);
                    }
                    if (data.detail?.airlineNotifyDate) {
                        data.detail.airlineNotifyDate = new Date(data.detail.airlineNotifyDate);
                        data.detail.airlineNotifyDateString = complaintService.dateString(data.detail.airlineNotifyDate);
                    }
                    // if (data.departments) {
                    //     data.ascDepartment = data.departments.find(o => o.departmentType === 'asc');
                    //     data.relatedMainDepartment = data.departments.find(o => o.departmentType === 'main')
                    //     const sub = data.departments.filter(o => o.departmentType === 'sub');
                    //     data.hasRelatedSubDepartment = sub && sub.length > 0;
                    //     data.relatedSubDepartments = sub;
                    // }
                    this.bindingFEModel(data);
                    this.complaintInfo = data;
                    this.validateResponsiblePerson();
                }
            } catch (error: any) {
                this.displayLoading = false;
                throw error;
            } finally {
                this.displayLoading = false;
            }
            this.loading = false;
        },
        async getSubTopic(id: number) {
            try {
                const res = await complaintService.getSubById(id.toString());
                if (res && res.status == 200) {
                    if (this.complaintInfo)
                        this.complaintInfo.form = res.data.formInfo;
                }
            } catch (error: any) {
                throw error;
            }
        },
        emptyForm() {
            let data = {
                mainId: undefined,
                subId: undefined,
                form: undefined,
            } as ComplaintInfo;
            return data;
        },
        emptyStep(data: ComplaintInfo) {
            if (this.haveFlightStep) {
                data.flight = {
                    departureDate: undefined,
                    departureDateString: null,
                    flightNo: undefined,
                    flightInfo: undefined,
                    airport: undefined,
                    bookings: [],
                    channel: {
                        mainTicketChannelId: undefined,
                        subTicketChannelId: undefined,
                        remark: undefined,
                    },
                } as ComplaintFlight;
                if (this.flight) {
                    this.flight.confirmFlight = false;
                    this.flight.getTicketChannels();
                }
            }
            if (this.havePassengerStep) {
                data.passengers = [
                    {
                        firstName: undefined,
                        middleName: undefined,
                        lastName: undefined,
                        nationality: undefined,
                        phoneNumber: undefined,
                        email: undefined,
                        published: false,
                        revealInfo: false,
                    }
                ] as PassengerInfo[];
            }
            if (this.haveComplaintStep) {
                data.detail = {
                    claimsModel: [] as SelectionFEModelType[],
                } as ComplaintDetail;
            }
            this.bindingFEModel(data);
        },
        bindingFEModel(data: ComplaintInfo) {
            if (!data.detail) {
                data.detail = {
                    claimsModel: [] as SelectionFEModelType[],
                } as ComplaintDetail
            } else {
                data.detail.claimsModel = [];
            }
            this.stepRequest.forEach((o, index) => {
                const activeClaim = data.detail.claims?.find(c => c.claimsNo === o.id);
                if (data.detail.claimsModel) {
                    if (activeClaim) {
                        data.detail.claimsModel.push({ id: o.id, active: true, remark: activeClaim.remark });
                    } else {
                        data.detail.claimsModel.push({ id: o.id, active: false });
                    }
                }
            });
        },
        async validFormAllSteps() {
            let validFlight = this.flightForm ? await this.flightForm.validate() : true;
            validFlight = validFlight.valid === undefined ? true : validFlight.valid;
            let validPassenger = this.passengerForm ? await this.passengerForm.validate() : true;
            validPassenger = validPassenger.valid === undefined ? true : validPassenger.valid;
            let validTopic = this.topicForm ? await this.topicForm.validate() : true;
            validTopic = validTopic.valid === undefined ? true : validTopic.valid;
            if (!validFlight) this.activeStep = this.flightStepNo ?? 1;
            else if (!validPassenger) this.activeStep = this.passengerStepNo ?? 1;
            else if (!validTopic) this.activeStep = this.complaintStepNo ?? 1;
            return validFlight && validPassenger && validTopic
        },
        async validCurrentStep(currentStep: number) {
            if (currentStep === this.flightStepNo) {
                let validFlight = this.flightForm ? await this.flightForm.validate() : true;
                validFlight = validFlight.valid === undefined ? true : validFlight.valid;
                if (!validFlight) this.activeStep = this.flightStepNo ?? 1;
                return validFlight;
            } else if (currentStep === this.passengerStepNo) {
                let validPassenger = this.passengerForm ? await this.passengerForm.validate() : true;
                validPassenger = validPassenger.valid === undefined ? true : validPassenger.valid;
                if (!validPassenger) this.activeStep = this.passengerStepNo ?? 1;
                return validPassenger;
            } else if (currentStep === this.complaintStepNo) {
                let validTopic = this.topicForm ? await this.topicForm.validate() : true;
                validTopic = validTopic.valid === undefined ? true : validTopic.valid;
                if (!validTopic) this.activeStep = this.complaintStepNo ?? 1;
                return validTopic;
            }
        },
        async uploadFileBeforeSave() {
            try {
                let allUploads = [] as AttachmentUploaderType[];
                if (this.complaintInfo && this.complaintInfo.flight) {
                    if (this.complaintInfo.flight.bookings && this.complaintInfo.flight.bookings.length > 0) {
                        // combine all files of all booking
                        for (const booking of this.complaintInfo.flight.bookings) {
                            if (booking.attachUploads && booking.attachUploads.length > 0) {
                                if (!booking.attachments) booking.attachments = [];
                                allUploads = allUploads.concat([...booking.attachUploads]);
                            }
                        }
                    }
                }
                if (this.complaintInfo && this.complaintInfo.detail) {
                    if (this.complaintInfo.detail.attachUploads && this.complaintInfo.detail.attachUploads.length > 0) {
                        if (!this.complaintInfo.detail.attachments) this.complaintInfo.detail.attachments = [];
                        allUploads = allUploads.concat([...this.complaintInfo.detail.attachUploads]);
                    }
                    if (this.complaintInfo.detail.airlineNotifyAttachUploads && this.complaintInfo.detail.airlineNotifyAttachUploads.length > 0) {
                        if (!this.complaintInfo.detail.airlineNotifyAttachments) this.complaintInfo.detail.airlineNotifyAttachments = [];
                        allUploads = allUploads.concat([...this.complaintInfo.detail.airlineNotifyAttachUploads]);
                    }
                }

                if (allUploads && allUploads.length > 0) {
                    // prepare payloads
                    let payloads = [] as File[];
                    allUploads.forEach(attach => {
                        if (attach.blob) {
                            const blob = fileService.dataURLToBlob(attach.blob);
                            let file = new File([blob], attach.name, { type: attach.fileType })
                            payloads.push(file);
                        }
                    });
                    // call upload API
                    const res = await fileService.upload(payloads);
                    if (res && res.status === 201) {
                        const data = res.data as UploadResponse;
                        if (!Array.isArray(data?.files) || data.files.length === 0) {
                            throw new Error('Upload response contains no files');
                        }

                        data.files.forEach(file => {
                            let attachment = {
                                itemNo: -1,
                                fileName: file.fileName,
                                runningNo: -1,
                                size: allUploads?.find(upload => upload.name === file.fileName)?.size,
                                url: file.objectName,
                            } as AttachmentUrl;

                            if (this.activeStep === this.flightStepNo) {
                                let booking = this.complaintInfo?.flight?.bookings.find(o => o.attachUploads?.find(f => f.name === attachment.fileName) !== undefined)
                                if (booking) {
                                    booking.attachments?.push(attachment);
                                } else {
                                    throw new Error();
                                }
                            } else if (this.activeStep === this.complaintStepNo) {
                                if (this.complaintInfo?.detail.attachUploads?.find(o => o.name === attachment.fileName) !== undefined) {
                                    this.complaintInfo?.detail.attachments?.push(attachment);
                                }
                                if (this.complaintInfo?.detail.airlineNotifyAttachUploads?.find(o => o.name === attachment.fileName) !== undefined) {
                                    this.complaintInfo?.detail.airlineNotifyAttachments?.push(attachment);
                                }
                            }
                        });

                        if (this.complaintInfo) {
                            // clear booking attachUploads
                            this.complaintInfo.flight?.bookings.forEach(bk => {
                                bk.attachUploads = [];
                            });
                            // clear attachment last step
                            this.complaintInfo.detail.airlineNotifyAttachUploads = [];
                            this.complaintInfo.detail.attachUploads = [];
                        }
                    } else {
                        throw new Error();
                    }
                }
            } catch (e: any) {
                throw new Error("Upload Error");
            }
        },
        modelToSave() {
            let model = {
                requestNo: this.complaintInfo?.requestNo,
                mainId: this.complaintInfo?.mainId,
                subId: this.complaintInfo?.subId,
                status: this.complaintInfo?.status,
                flight: this.complaintInfo?.flight ? {
                    flightNo: this.complaintInfo?.flight.flightNo,
                    departureDate: this.complaintInfo?.flight.departureDate,
                    flightInfo: this.complaintInfo?.flight.flightInfo,
                    bookings: this.complaintInfo?.flight.bookings,
                    channel: {
                        mainTicketChannelId: this.complaintInfo?.flight.channel.mainTicketChannelId,
                        mainTicketChannelOptional: this.complaintInfo?.flight.channel.mainTicketChannelOptional,
                        mainTicketFormatChannelOptional: this.complaintInfo?.flight.channel.mainTicketFormatChannelOptional,
                        subTicketChannelId: this.complaintInfo?.flight.channel.subTicketChannelId,
                        remark: this.complaintInfo?.flight.channel.remark,
                    } as ComplaintBuyTicketSave
                } as ComplaintFlightSave : {},
                passengers: this.complaintInfo?.passengers,
                detail: this.complaintInfo?.detail,
                relatedMainDepartment: this.complaintInfo?.relatedMainDepartment,
                hasRelatedSubDepartment: this.complaintInfo?.hasRelatedSubDepartment,
                relatedSubDepartments: this.complaintInfo?.relatedSubDepartments,
                ascDepartment: this.complaintInfo?.ascDepartment,
                complainant: this.complaintInfo?.complainant,
                remark: this.complaintInfo?.remark,
            } as ComplaintInfoSave;
            if (!this.haveFlightStep)
                model.flight = {} as ComplaintFlightSave;
            if (!this.havePassengerStep)
                model.passengers = [];
            if (!this.haveComplaintStep)
                model.detail = {} as ComplaintDetail;
            return model;
        },
        async save(isAuto: boolean = false, saveFromNext: boolean = true): Promise<boolean> {
            try {
                let saveSuccess = false;
                const valid = await this.validCurrentStep(this.activeStep);
                if (valid && this.complaintInfo) {
                    await this.uploadFileBeforeSave()
                    let res;
                    let model = this.modelToSave();
                    if (this.isNew) {
                        if (this.haveFlightStep) {
                            model.passengers = [];
                            model.detail = {} as ComplaintDetail;
                        } else if (this.havePassengerStep) {
                            model.flight = {} as ComplaintFlightSave;
                            model.detail = {} as ComplaintDetail;
                        } else if (this.haveComplaintStep) {
                            model.flight = {} as ComplaintFlightSave;
                            model.passengers = [];
                        }
                        // sent only on create request
                        model.complainant = {
                            itemNo: -1,
                            title: userService.currentUserInfo()?.title,
                            firstName: userService.currentUserInfo()?.firstname,
                            middleName: userService.currentUserInfo()?.midname,
                            lastName: userService.currentUserInfo()?.lastname,
                            phoneNumber: userService.currentUserInfo()?.tel,
                            email: userService.currentUserInfo()?.email,
                            published: false,
                            revealInfo: true,
                            nationality: undefined
                        }
                        res = await complaintService.createRequest(model)
                    } else if (this.complaintInfo.requestNo) {
                        if (this.havePassengerStep && model.passengers.length > 0) {
                            model.passengers = model.passengers.filter(p => {
                                if (p.itemNo === -1 && !p.title && !p.firstName && !p.lastName && !p.middleName && !p.email && !p.nationality && !p.phoneNumber) {
                                    return false;
                                }
                                return true;
                            });
                        }
                        res = await complaintService.updateRequest(this.complaintInfo.requestNo, model);
                    }
                    if (res && res.status === 200) {
                        saveSuccess = true;
                        this.currentRequestNo = res.data.requestNo;
                        if (!isAuto) {
                            this.successAlert.header = `${this.$t('t-save-draft')}`;
                            this.successAlert.icon = 'mdi mdi-content-save-outline';
                            this.successAlert.messageTitle = `${this.$t('t-save-draft')}${this.$t('t-already')}`;
                            this.successAlert.messageText = `${this.$t('t-complaint-view-saved-complaint-at')} ${this.$t('t-complaint-status')}`;
                            this.successAlert.display = true;
                            if (saveFromNext)
                                this.successAlert.callBack = this.goToRequestPage;
                            else
                                this.successAlert.callBack = this.goToRequestListPage;
                        }
                    }
                }
                return saveSuccess;
            } catch (e: any) {
                if (e.message === 'Upload Error') {
                    this.failAlert.messageTitle = this.$t('t-upload-file-fail');
                } else {
                    this.failAlert.messageTitle = `${this.$t('t-save-draft')}${this.$t('t-fail')}`;
                }
                this.failAlert.header = `${this.$t('t-save-draft')}${this.$t('t-fail')}`;
                this.failAlert.messageText = undefined;
                this.failAlert.display = true;
                return false;
            }
        },
        async preSubmit() {
            const valid = await this.validFormAllSteps();
            if (valid) {
                if (this.validBaggage) {
                    this.summary.header = this.$t('t-complaint-submit-confirm');
                    this.summary.callBack = this.submit;
                    this.summary.alert = true;
                } else {
                    this.failAlert.header = undefined;
                    this.failAlert.messageTitle = this.$t('t-complaint-request-reason-required')
                    this.failAlert.messageText = undefined;
                    this.failAlert.display = true;
                }
            } else {
                this.failAlert.header = undefined;
                this.failAlert.messageTitle = this.$t('t-complaint-submit-missing-info');
                this.failAlert.messageText = undefined;
                this.failAlert.display = true;
            }
        },
        async submit() {
            if (this.isSubmitting) return;

            this.isSubmitting = true;
            try {
                if (this.complaintInfo) {
                    await this.uploadFileBeforeSave();
                    let model = this.modelToSave();
                    if (!model.requestNo) model.requestNo = 'new';
                    // send only on submit request
                    model.submitterTitle = userService.currentUserInfo()?.title;
                    model.submitterFirstName = userService.currentUserInfo()?.firstname;
                    model.submitterMiddleName = userService.currentUserInfo()?.midname;
                    model.submitterLastName = userService.currentUserInfo()?.lastname;
                    let res = await complaintService.submitRequest(model);
                    if (res && res.status === 200) {
                        this.summary.alert = false;
                        this.successAlert.header = `${this.$t('t-complaint')}${this.$t('t-success')}`;
                        this.successAlert.icon = undefined;
                        this.successAlert.messageTitle = `${this.$t('t-complaint')}${this.$t('t-already')}`;
                        this.successAlert.messageText = `${this.$t('t-complaint-view-saved-complaint-at')} ${this.$t('t-complaint-status')}`;
                        this.successAlert.display = true;
                        this.successAlert.callBack = this.goToRequestListPage;
                    }
                }
            } catch (e: any) {
                if (e.message === 'Upload Error') {
                    this.failAlert.messageTitle = this.$t('t-upload-file-fail');
                } else {
                    this.failAlert.messageTitle = `${this.$t('t-complaint')}${this.$t('t-fail')}`;
                }
                this.failAlert.header = `${this.$t('t-complaint')}${this.$t('t-fail')}`;
                this.failAlert.messageText = undefined;
                this.failAlert.display = true;
            } finally {
                this.isSubmitting = false;
            }
        },
        resetData() {
            this.confirm = {
                messageTitle: `${this.$t('t-you-need')}${this.$t('t-clear')}${this.$t('t-or-not')}`,
                messageText: this.$t('t-complaint-confirm-clear-data'),
                callBack: this.resetForm,
                display: true
            } as BaseConfirm;
        },
        resetForm() {
            const prev = {
                requestNo: this.complaintInfo?.requestNo,
                requestStatus: this.complaintInfo?.status ?? '',
                form: this.complaintInfo?.form,
                mainId: this.complaintInfo?.mainId,
                mainTopicTh: this.complaintInfo?.mainTopicTh,
                mainTopicEn: this.complaintInfo?.mainTopicEn,
                subId: this.complaintInfo?.subId,
                subTopicTh: this.complaintInfo?.subTopicTh,
                subTopicEn: this.complaintInfo?.subTopicEn
            };
            this.complaintInfo = { ...this.emptyForm() };
            this.complaintInfo.requestNo = prev.requestNo;
            this.complaintInfo.status = prev.requestStatus;
            this.complaintInfo.form = prev.form;
            this.complaintInfo.mainId = prev.mainId;
            this.complaintInfo.mainTopicTh = prev.mainTopicTh;
            this.complaintInfo.mainTopicEn = prev.mainTopicEn;
            this.complaintInfo.subId = prev.subId;
            this.complaintInfo.subTopicTh = prev.subTopicTh;
            this.complaintInfo.subTopicEn = prev.subTopicEn;
            this.emptyStep(this.complaintInfo);
            this.activeStep = 1;
        },
        resetBreadcrumb() {
            this.breadcrumb.forEach((o, index) => {
                if (index < this.activeStep) {
                    o.active = true;
                } else {
                    o.active = false;
                }
            })
        },
        async next() {
            if (this.isMovingNext) return;

            this.isMovingNext = true;
            try {
                if (this.activeStep === this.passengerStepNo) {
                    await this.passengerForm.validate().then(async (validate: any) => {
                        if (validate.valid) {
                            if (this.isNew) {
                                this.confirm = {
                                    messageTitle: this.$t('t-confirm-auto-save'),
                                    messageText: undefined,
                                    callBack: this.confirmAutoSave,
                                    display: true
                                } as BaseConfirm;
                            } else {
                                // auto save
                                const saveSuccess = await this.save(true);
                                if (saveSuccess) {
                                    this.activeStep += 1;
                                }
                            }
                        }
                    });
                } else if (this.activeStep === this.flightStepNo) {
                    await this.flightForm.validate().then(async (validate: any) => {
                        if (validate.valid) {
                            if (this.isNew) {
                                this.confirm = {
                                    messageTitle: this.$t('t-confirm-auto-save'),
                                    messageText: undefined,
                                    callBack: this.confirmAutoSave,
                                    display: true
                                } as BaseConfirm;
                            } else {
                                // auto save
                                const saveSuccess = await this.save(true);
                                if (saveSuccess) {
                                    this.activeStep += 1;
                                }
                            }
                        }
                    });
                }
            } finally {
                this.isMovingNext = false;
            }
        },
        async confirmAutoSave() {
            if (this.isMovingNext) return;

            this.isMovingNext = true;
            try {
                await this.save();
            } finally {
                this.isMovingNext = false;
            }
        },
        prev() {
            if (this.activeStep > 1)
                this.activeStep -= 1;
            else if (!this.viewMode) {
                if (this.isNew) {
                    this.confirm = {
                        messageTitle: this.$t('t-complaint-cancel-create'),
                        callBack: this.goPrevPage,
                        display: true
                    } as BaseConfirm;
                }
                else {
                    this.confirm = {
                        messageTitle: this.$t('t-complaint-cancel-edit'),
                        callBack: this.goPrevPage,
                        display: true
                    } as BaseConfirm;
                }
            } else {
                this.goPrevPage();
            }
        },
        goToRequestListPage() {
            this.store.setMyData({ reloadDoc: false });
            this.router.push({
                path: `/complaint-status`
            })
        },
        goToRequestPage() {
            this.store.setMyData({ reloadDoc: true });
            this.router.push({
                path: `/complaint/${this.currentRequestNo}`
            })
        },
        goPrevPage() {
            this.router.go(-1);
        },
        runCallbackConfirm(item: any) {
            if (item.callBack)
                item.callBack();
        },
        runCallbackCancel(item: any) {
            if (item.callBackCancel)
                item.callBackCancel();
        },
        displayConfirmDialog(model: BaseConfirm) {
            this.confirm = { ...model };
            this.confirm.display = true;
        },
        displayAlertDialog(model: BaseAlert) {
            if (model.success) {
                this.successAlert = model;
                this.successAlert.display = true;
            } else {
                this.failAlert = model;
                this.failAlert.display = true;
            }
        },
        resetValidation() {
            if (this.flightForm)
                this.flightForm.resetValidation();
            if (this.passengerForm)
                this.passengerForm.resetValidation();
            if (this.topicForm)
                this.topicForm.resetValidation();
        },
        async getFormStepComplaint() {
            try {
                const res = await formService.getAllSteps();
                if (res && res.status == 200) {
                    const result = res.data as FormStep;
                    this.formStepComplaint = result.stepThree;
                }
            } catch (e: any) {
                throw e;
            }
        },
        validateResponsiblePerson() {
            if (this.isMainDpm) {
                const persons = this.complaintInfo?.relatedMainDepartment?.responiblePersons;
                if (!persons || persons.length <= 0) {
                    this.displayWarningResponsiblePerson();
                }
            } else if (this.isSubDpm) {
                const persons = this.complaintInfo?.relatedSubDepartments
                    ?.find(o => o.departmentCode === userService.currentDepartment())?.responiblePersons;
                if (!persons || persons.length <= 0) {
                    this.displayWarningResponsiblePerson();
                }
            }
        },
        displayWarningResponsiblePerson() {
            this.successAlert.header = undefined;
            this.successAlert.icon = "mdi mdi-alert-circle-outline";
            this.successAlert.messageTitle = this.$t('t-complaint-no-caretaker');
            this.successAlert.messageText = undefined;
            this.successAlert.display = true;
        },
        exportToPDF() {
            let element;
            if (userService.isAsc() && this.dtAsc)
                element = this.dtAsc.getPdfContent();
            else if (userService.isDpm() && this.dtDpm)
                element = this.dtDpm.getPdfContent();
            else if (userService.isAirline() && this.dtAir)
                element = this.dtAir.getPdfContent();

            if (element) {

                // Select buttons and v-file-input elements
                const buttons = element.querySelectorAll('button');
                const fileInputs = element.querySelectorAll('.upload-section');
                const textareas = element.querySelectorAll('textarea');
                const divText = element.querySelectorAll('.textarea-export');

                // Hide elements, store original display
                const hideElements = (elements: any) => {
                    elements.forEach((el: any) => {
                        el.dataset.originalDisplay = el.style.display; // Store original display
                        el.style.display = 'none';
                    });
                };

                // Add block to textareas
                const styleBlocks = (elements: any) => {
                    elements.forEach((el: any) => {
                        el.dataset.originalDisplay = el.style.display;
                        el.style.display = 'block';
                    });
                };

                // Restore elements to original display
                const restoreElements = (elements: any) => {
                    elements.forEach((el: any) => {
                        el.style.display = el.dataset.originalDisplay || ''; // Restore from stored value
                    });
                };

                hideElements([...buttons, ...fileInputs, ...textareas]);
                styleBlocks([...divText]);

                const opt = {
                    margin: [5, 5, 5, 5],
                    filename: `${this.complaintInfo?.complaintNo}.pdf`,
                    image: { type: 'jpeg', quality: 0.98 },
                    html2canvas: { scale: 2, useCORs: true },
                    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                };

                // html2pdf().from(element).set(opt).save();

                html2pdf().from(element).set(opt).save().then(() => {
                    // Restore buttons and v-file-input elements
                    restoreElements([...buttons, ...fileInputs, ...textareas]);
                    hideElements([...divText]);
                }).catch(() => {
                    // Ensure restore on error
                    restoreElements([...buttons, ...fileInputs, ...textareas]);
                    hideElements([...divText]);
                });
            }
        },
        async newExportPdf() {
            if (this.complaintInfo?.requestNo) {
                const res = await complaintService.exportPdf(this.complaintInfo?.requestNo, this.lang)
                if (res && res.status === 200) {
                    const blob = new Blob([res.data], { type: 'application/pdf' });
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = `complaint-${this.complaintInfo.complaintNo}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            }
        }
    },
};
</script>

<template>
    <div class="bg-page-home main-page"></div>
    <div v-if="!viewMode" class="bg-page-home-content complaint-grid complaint-detail">
        <Breadcrumb :title="$t('t-complaint')" :items="breadcrumb" :is-step="true" />
        <RequestHeader v-if="!isNew && complaintInfo" v-model="complaintInfo">
        </RequestHeader>
        <v-stepper v-model="activeStep" :next-text="$t('t-next')" :prev-text="$t('t-back')">
            <v-form class="app-form-input">
                <!-- <template v-slot:default="{ prev, next }"> -->
                <v-stepper-header>
                    <template v-for="n in breadcrumb.length" :key="`${n}-step`">
                        <v-stepper-item :step="`Step {{ n }}`" :value="n">
                        </v-stepper-item>
                    </template>
                </v-stepper-header>

                <v-stepper-window v-if="haveFlightStep && flightStepNo">
                    <v-stepper-window-item :value="flightStepNo">
                        <v-form ref="flightForm">
                            <Flight ref="flight" v-if="complaintInfo" v-model="complaintInfo" :view-mode="viewMode"
                                @display-confirm-dialog="displayConfirmDialog"
                                @display-alert-dialog="displayAlertDialog" @reset-validation="resetValidation">
                            </Flight>
                        </v-form>
                    </v-stepper-window-item>
                </v-stepper-window>

                <v-stepper-window v-if="havePassengerStep && passengerStepNo">
                    <v-stepper-window-item :value="passengerStepNo">
                        <v-form ref="passengerForm">
                            <Passenger ref="passenger" v-if="complaintInfo" v-model="complaintInfo"
                                :view-mode="viewMode" @display-confirm-dialog="displayConfirmDialog">
                            </Passenger>
                        </v-form>
                    </v-stepper-window-item>
                </v-stepper-window>

                <v-stepper-window v-if="haveComplaintStep && complaintStepNo">
                    <v-stepper-window-item :value="complaintStepNo">
                        <v-form ref="topicForm">
                            <Topic v-if="complaintInfo" v-model="complaintInfo" :view-mode="viewMode"
                                @reset-validation="resetValidation" @display-alert-dialog="displayAlertDialog">
                            </Topic>
                        </v-form>
                    </v-stepper-window-item>
                </v-stepper-window>
                <v-stepper-actions class="complaint-footer">
                    <template v-slot:next>
                        <div class="d-flex justify-end">
                            <v-btn v-if="!viewMode" class="mr-2 reset-btn" variant="outlined" color="primary"
                                size="large" :disabled="false" @click="resetData">
                                {{ $t('t-clear') }}</v-btn>
                            <v-btn v-if="!viewMode && activeStep >= breadcrumb.length" class="save-btn 
                                mr-2" variant="outlined" color="primary" size="large" :disabled="false"
                                @click="save(false, false)">
                                {{ $t('t-save') }}</v-btn>
                            <v-menu>
                                <template v-slot:activator="{ props }">
                                    <v-btn style="display: none;" variant="outlined" class="list-btn-mobile mr-2"
                                        color="primary" v-bind="props" size="large" :disabled="false">
                                        <v-icon icon="mdi mdi-format-list-bulleted"></v-icon>
                                    </v-btn>
                                </template>
                                <v-list>
                                    <v-list-item v-if="!viewMode" :disabled="false" @click="resetData">
                                        <v-list-item-title>
                                            <v-icon icon="mdi mdi-close"></v-icon>
                                            {{ $t('t-clear') }}
                                        </v-list-item-title>
                                    </v-list-item>
                                    <v-list-item v-if="!viewMode && activeStep >= breadcrumb.length" :disabled="false"
                                        @click="save(false, false)">
                                        <v-list-item-title>
                                            <v-icon icon="mdi mdi-content-save-check-outline"></v-icon>
                                            {{ $t('t-save') }}
                                        </v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                            <v-btn v-if="activeStep < breadcrumb.length" :disabled="disabled || isMovingNext || isSubmitting" :loading="isMovingNext" variant="flat"
                                color="primary" size="large" append-icon="mdi mdi-chevron-right" @click="next">
                                {{ $t('t-next') }}
                            </v-btn>
                            <v-btn v-if="activeStep >= breadcrumb.length && completed && !viewMode"
                                :disabled="!validTopic || isSubmitting" type="button" variant="flat" color="primary" size="large"
                                append-icon="mdi mdi-send-outline" @click="preSubmit">
                                {{ $t('t-complaint-submit') }}
                            </v-btn>
                        </div>
                    </template>
                    <template v-slot:prev>
                        <v-btn class="mr-2 prev" variant="outlined" color="primary" size="large"
                            prepend-icon="mdi mdi-arrow-left" :disabled="false" @click="prev">
                            {{ $t('t-back') }}</v-btn>
                    </template>
                </v-stepper-actions>
                <!-- </template> -->
            </v-form>
        </v-stepper>
        <!-- Summary -->
        <Summary v-model="summary.alert" :header="summary.header" :complaint="complaintInfo"
            :have-step-flight="haveFlightStep" :have-step-passenger="havePassengerStep"
            :have-step-complaint="haveComplaintStep" :step-flight-no="flightStepNo" :step-passenger-no="passengerStepNo"
            :step-complaint-no="complaintStepNo" @on-confirm="runCallbackConfirm(summary)">
        </Summary>
    </div>
    <!-- Success -->
    <AlertDialog v-model="successAlert.display" :header="successAlert.header" :title="successAlert.messageTitle"
        :text="successAlert.messageText" :success-icon="successAlert.icon"
        @on-confirm="runCallbackConfirm(successAlert)">
    </AlertDialog>
    <!-- Fail -->
    <AlertDialog v-model="failAlert.display" :header="failAlert.header" :title="failAlert.messageTitle"
        :text="failAlert.messageText" :success="false">
    </AlertDialog>
    <!-- Confirm Delete -->
    <ConfirmDialog v-model="confirm.display" :header="confirm.header" :title="confirm.messageTitle"
        :text="confirm.messageText" :confirm-text="confirm.confirmBtnText" :cancel-text="confirm.cancelBtnText"
        @on-confirm="runCallbackConfirm(confirm)" @on-cancel="runCallbackCancel(confirm)">
    </ConfirmDialog>
    <!-- Detail View Mode (for user) -->
    <DetailView v-if="viewMode && userSv.isUser() && !userSv.isAsc() && !userSv.isDpm()" v-model="complaintInfo"
        @display-confirm-dialog="displayConfirmDialog" @display-alert-dialog="displayAlertDialog">
    </DetailView>
    <!-- Detail View Mode (for ASC) -->
    <DetailAsc ref="dtAsc" v-if="viewMode && isAscWithNotResponsible" v-model="complaintInfo"
        @display-confirm-dialog="displayConfirmDialog" @display-alert-dialog="displayAlertDialog"
        @export-pdf="newExportPdf">
    </DetailAsc>
    <!-- Detail View Mode (for Department) -->
    <DetailDpm ref="dtDpm"
        v-if="viewMode && (userSv.isDpm() || !isAscWithNotResponsible) && !userSv.isAirline() && !userSv.isUser()"
        v-model="complaintInfo" @display-confirm-dialog="displayConfirmDialog"
        @display-alert-dialog="displayAlertDialog" @export-pdf="newExportPdf">
    </DetailDpm>
    <!-- Detail View Mode (for Airline) -->
    <DetailAirline ref="dtAir" v-if="viewMode && userSv.isAirline()" v-model="complaintInfo"
        @display-confirm-dialog="displayConfirmDialog" @display-alert-dialog="displayAlertDialog"
        @export-pdf="newExportPdf">
    </DetailAirline>
    <!-- Loading -->
    <v-overlay persistent :model-value="displayLoading || isSubmitting || isMovingNext" class="align-center justify-center">
        <v-progress-circular color="primary" size="64" indeterminate></v-progress-circular>
    </v-overlay>
</template>
